import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { Reveal, SectionHead } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, testId, ...props }) => (
  <div>
    <label className="elt-label">{label}</label>
    <input data-testid={testId} className="elt-input" {...props} />
  </div>
);

export const Corporate = () => {
  const { t } = useI18n();
  const [form, setForm] = useState({ company: "", contact_name: "", email: "", phone: "", monthly_volume: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact_name || !form.email) return toast.error(t("form.required"));
    setSending(true);
    try {
      await axios.post(`${API}/requests/corporate`, form);
      toast.success(t("form.corporateSuccess"));
      setForm({ company: "", contact_name: "", email: "", phone: "", monthly_volume: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    setSending(false);
  };

  return (
    <section id="corporate" className="bg-[#0B1320] border-y border-white/5">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-28 lg:py-36 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-6">
          <SectionHead eyebrow={t("corporate.eyebrow")} title={t("corporate.title")} />
          <Reveal delay={0.12}>
            <p className="mt-6 text-sm font-light text-[var(--elt-muted)] max-w-md leading-relaxed">{t("corporate.body")}</p>
            <div className="mt-8 flex flex-wrap gap-2">
              {t("corporate.items").map((s) => (
                <span key={s} className="text-[0.65rem] tracking-[0.2em] uppercase text-[var(--elt-ivory)]/70 border border-white/10 px-4 py-2">{s}</span>
              ))}
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-5 lg:col-start-8">
          <Reveal delay={0.18}>
            <form onSubmit={submit} data-testid="corporate-form" className="bg-[#0B0C0E]/60 border border-white/8 p-8 lg:p-10 space-y-6 backdrop-blur">
              <p className="eyebrow">{t("cta.openCorporate")}</p>
              <Field label={t("form.company")} testId="corporate-company-input" value={form.company} onChange={set("company")} />
              <Field label={t("form.contactName")} testId="corporate-contact-input" value={form.contact_name} onChange={set("contact_name")} />
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label={t("form.email")} testId="corporate-email-input" type="email" value={form.email} onChange={set("email")} />
                <Field label={t("form.phone")} testId="corporate-phone-input" value={form.phone} onChange={set("phone")} />
              </div>
              <Field label={t("form.monthlyVolume")} testId="corporate-volume-input" value={form.monthly_volume} onChange={set("monthly_volume")} />
              <div>
                <label className="elt-label">{t("form.message")}</label>
                <textarea data-testid="corporate-message-input" className="elt-input resize-none" rows={3} value={form.message} onChange={set("message")} />
              </div>
              <button type="submit" disabled={sending} data-testid="corporate-submit-btn" className="btn-gold w-full justify-center disabled:opacity-50">
                {t("cta.openCorporate")}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const Partners = () => {
  const { t } = useI18n();
  const types = t("form.partnerTypes");
  const [form, setForm] = useState({ company: "", partner_type: "", contact_name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact_name || !form.email || !form.partner_type) return toast.error(t("form.required"));
    setSending(true);
    try {
      await axios.post(`${API}/requests/partner`, form);
      toast.success(t("form.partnerSuccess"));
      setForm({ company: "", partner_type: "", contact_name: "", email: "", phone: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    setSending(false);
  };

  return (
    <section id="partners" className="max-w-[1440px] mx-auto px-6 lg:px-10 py-28 lg:py-36 grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <SectionHead eyebrow={t("partners.eyebrow")} title={t("partners.title")} />
        <Reveal delay={0.12}>
          <p className="mt-6 text-sm font-light text-[var(--elt-muted)] max-w-md leading-relaxed">{t("partners.body")}</p>
        </Reveal>
      </div>
      <div className="lg:col-span-6 lg:col-start-7">
        <Reveal delay={0.18}>
          <form onSubmit={submit} data-testid="partner-form" className="border border-[var(--elt-line)] p-8 lg:p-10 space-y-6">
            <p className="eyebrow">{t("cta.becomePartner")}</p>
            <Field label={t("form.company")} testId="partner-company-input" value={form.company} onChange={set("company")} />
            <div>
              <label className="elt-label">{t("form.partnerType")}</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {types.map((p) => (
                  <button type="button" key={p} data-testid={`partner-type-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setForm({ ...form, partner_type: p })}
                    className={`text-[0.62rem] tracking-[0.18em] uppercase px-3 py-2 border transition-colors duration-300 ${form.partner_type === p ? "border-[var(--elt-gold)] text-[var(--elt-gold-light)]" : "border-white/12 text-[var(--elt-muted)] hover:border-white/30"}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <Field label={t("form.contactName")} testId="partner-contact-input" value={form.contact_name} onChange={set("contact_name")} />
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label={t("form.email")} testId="partner-email-input" type="email" value={form.email} onChange={set("email")} />
              <Field label={t("form.phone")} testId="partner-phone-input" value={form.phone} onChange={set("phone")} />
            </div>
            <div>
              <label className="elt-label">{t("form.message")}</label>
              <textarea data-testid="partner-message-input" className="elt-input resize-none" rows={3} value={form.message} onChange={set("message")} />
            </div>
            <button type="submit" disabled={sending} data-testid="partner-submit-btn" className="btn-ghost w-full justify-center disabled:opacity-50 hover:!border-[var(--elt-gold)]">
              {t("cta.becomePartner")}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
